import * as React from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EventScheduleFields } from '@/components/events/event-schedule-fields'
import { type EventDetails } from '@/components/events/event-details-form'

export function EventContactControl({
  name,
  email,
  editing,
  onEdit,
  onNameChange,
  onEmailChange,
  onClear,
}: {
  name: string
  email: string
  editing: boolean
  onEdit: () => void
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onClear: () => void
}) {
  if (!editing && !name && !email) {
    return (
      <Button type="button" variant="outline" className="h-7 border-dashed px-2" onClick={onEdit}>
        + Add event contact
      </Button>
    )
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-body-sm">
          {name} · {email}
        </span>
        <Button
          type="button"
          variant="outline"
          className="h-8 w-8 border-transparent p-0 text-muted-foreground"
          aria-label="Edit event contact"
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="event-contact-name" className="text-caption text-muted-foreground">
            Name
          </Label>
          <Input
            id="event-contact-name"
            aria-label="Event contact name"
            className="h-8"
            value={name}
            onChange={(inputEvent) => onNameChange(inputEvent.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="event-contact-email" className="text-caption text-muted-foreground">
            Email
          </Label>
          <Input
            id="event-contact-email"
            aria-label="Event contact email"
            type="email"
            className="h-8"
            value={email}
            onChange={(inputEvent) => onEmailChange(inputEvent.target.value)}
          />
        </div>
      </div>
      <Button type="button" variant="outline" className="h-7 px-2" onClick={onClear}>
        Clear event contact
      </Button>
    </div>
  )
}

export function IdentityAndScheduleFields({
  event,
  name,
  startsAt,
  endsAt,
  onNameChange,
  onStartChange,
  onEndChange,
}: {
  event: EventDetails
  name: string
  startsAt: string
  endsAt: string
  onNameChange: (value: string) => void
  onStartChange: (value: string) => void
  onEndChange: (value: string) => void
}) {
  return (
    <>
      <DetailField label="Name" htmlFor={event.status === 'draft' ? 'event-edit-name' : undefined}>
        {event.status === 'draft' ? (
          <Input
            id="event-edit-name"
            className="h-8"
            value={name}
            onChange={(inputEvent) => onNameChange(inputEvent.target.value)}
          />
        ) : (
          <p className="py-1.5 text-body-sm">{event.name}</p>
        )}
      </DetailField>
      <EventScheduleFields
        startsAt={startsAt}
        endsAt={endsAt}
        onStartChange={onStartChange}
        onEndChange={onEndChange}
      />
    </>
  )
}

export function DetailField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-start">
      <Label htmlFor={htmlFor} className="pt-1.5 text-caption text-muted-foreground">
        {label}
      </Label>
      <div className="min-w-0">{children}</div>
    </div>
  )
}
